
import React from 'react';

const ProcessStep: React.FC<{ num: string; title: string; description: string; steps: string[] }> = ({ num, title, description, steps }) => (
  <div className="relative pl-16 sm:pl-24 pb-20 sm:pb-40 group last:pb-0">
    <div className="absolute left-0 top-0 w-12 h-12 sm:w-16 sm:h-16 rounded-2xl glass-card border border-white/10 flex items-center justify-center text-2xl sm:text-4xl font-black text-[#32dee6] group-hover:bg-[#32dee6] group-hover:text-black transition-all">
      {num}
    </div>
    <div className="absolute left-6 sm:left-8 top-12 sm:top-16 bottom-0 w-px bg-white/5 group-last:hidden"></div>
    
    <div className="grid lg:grid-cols-2 gap-10 sm:gap-20">
      <div className="space-y-4 sm:space-y-6">
        <h3 className="text-2xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight uppercase">{title}</h3>
        <p className="text-lg sm:text-xl text-gray-400 font-light leading-relaxed">{description}</p>
      </div>
      <div className="space-y-3 sm:space-y-4">
        {steps.map((step, i) => (
          <div key={i} className="p-4 sm:p-6 rounded-2xl bg-white/[0.02] border border-white/5 mono text-[9px] sm:text-[11px] font-bold text-gray-300 uppercase tracking-[0.2em] sm:tracking-widest flex items-center justify-between group/item hover:border-[#32dee6]/30 transition-all">
            <span>{step}</span>
            <div className="w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full bg-white/10 group-hover/item:bg-[#32dee6]"></div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ProcessPage: React.FC = () => {
  return (
    <div className="pt-32 sm:pt-52 pb-16 sm:pb-32 px-4 sm:px-6">
      <div className="container mx-auto max-w-6xl">
        <header className="mb-20 sm:mb-40 text-center px-4">
          <div className="mono text-[9px] sm:text-[10px] font-bold text-[#32dee6] tracking-[0.3em] sm:tracking-[0.4em] uppercase mb-4 sm:mb-6">Workflow_Optimization</div>
          <h1 className="text-5xl sm:text-7xl md:text-9xl font-black tracking-tighter text-white leading-none uppercase mb-6 sm:mb-12">
            The <br />
            <span className="gradient-text">Blueprint.</span>
          </h1>
          <p className="text-lg sm:text-2xl text-gray-500 font-light max-w-2xl mx-auto">
            A white-glove deployment methodology designed to integrate high-fidelity AI into your business with zero friction.
          </p>
        </header>

        <div className="relative">
          <ProcessStep 
            num="1"
            title="Infrastructure Audit"
            description="We conduct a deep-dive analysis of your current lead flow, telephony stack, and administrative bottlenecks. This identifies the highest-impact nodes for automation."
            steps={["Lead Source Mapping", "Telephony Stack Audit", "Bottleneck Identification", "ROI Projection"]}
          />
          <ProcessStep 
            num="2"
            title="Custom Configuration"
            description="Our engineers build your custom AI logic. This includes voice training, prompt engineering for industry-specific knowledge, and seamless API bridge setup."
            steps={["Voice Engine Training", "Prompt Engineering", "CRM Integration Setup", "UAT Testing Phases"]}
          />
          <ProcessStep 
            num="3"
            title="Deploy & Scale"
            description="Systems go live under monitored production environments. We ensure 24/7 uptime and provide real-time performance analytics for continuous optimization."
            steps={["Production Launch", "24/7 System Monitoring", "Performance Analytics", "Iterative Optimization"]}
          />
        </div>
      </div>
    </div>
  );
};

export default ProcessPage;